'use client';

import { useMemo, useState } from 'react';
import { X, Check, Search, ChevronDown } from 'lucide-react';
import { WIDGET_REGISTRY, WIDGET_ORDER } from '../widgets/registry';
import { CATEGORY_LABELS, type WidgetCategory } from '../widgets/types';

interface Props {
  open: boolean;
  visible: string[];
  onClose: () => void;
  onAdd: (id: string) => void;
  onReset: () => void;
}

// Căutare tolerantă la diacritice: „facturi restante" găsește „Facturi restante"
// și invers, indiferent cum tastează utilizatorul.
const norm = (s: string) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

export function WidgetPicker({ open, visible, onClose, onAdd, onReset }: Props) {
  const [q, setQ] = useState('');
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const grouped = useMemo(() => {
    const g: Partial<Record<WidgetCategory, string[]>> = {};
    const nq = norm(q.trim());
    for (const id of WIDGET_ORDER) {
      const def = WIDGET_REGISTRY[id];
      // (bug istoric: aici era `return null` în loc de `continue` — un id lipsă
      // din registry ar fi anulat TOT memo-ul și crăpa Object.entries(null))
      if (!def) continue;
      if (nq && !norm(def.title + ' ' + def.description).includes(nq)) continue;
      if (!g[def.category]) g[def.category] = [];
      g[def.category]!.push(id);
    }
    return g;
  }, [q]);

  if (!open) return null;
  const searching = q.trim().length > 0;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-[6px] p-6 animate-in fade-in duration-200"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-neutral-900 rounded-[18px] max-w-[920px] w-full max-h-[80vh] flex flex-col overflow-hidden shadow-[0_40px_80px_rgba(0,0,0,0.3)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-5 border-b border-neutral-200 dark:border-neutral-800 flex items-center justify-between gap-4">
          <div>
            <h2 className="m-0 text-[1.375rem] font-semibold tracking-[-0.01em]" style={{ fontFamily: 'var(--font-poppins), sans-serif' }}>
              Personalizează <em style={{ fontStyle: 'italic', color: 'rgb(var(--primary-500))', fontWeight: 500 }}>dashboard-ul</em>
            </h2>
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">Adaugă sau elimină widget-uri. Ordinea se salvează automat.</p>
          </div>
          <button
            onClick={onClose}
            className="h-8 w-8 rounded-lg bg-neutral-100 dark:bg-neutral-800 grid place-items-center hover:bg-neutral-200 dark:hover:bg-neutral-700 transition-colors"
            aria-label="Închide"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Căutare — 29 de widget-uri în 7 categorii erau greu de scanat fără filtru */}
        <div className="px-6 pt-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-neutral-400" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Caută widget… (ex. restanțe, TVA, recurențe)"
              autoFocus
              className="w-full rounded-lg border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 pl-9 pr-3 py-2 text-sm outline-none focus:border-primary focus:bg-white dark:focus:bg-neutral-900 transition-colors"
            />
          </div>
        </div>

        <div className="overflow-y-auto px-6 pb-6 flex-1">
          {Object.keys(grouped).length === 0 && (
            <p className="pt-8 text-center text-sm text-neutral-400">Niciun widget nu se potrivește cu „{q}".</p>
          )}
          {(Object.entries(grouped) as unknown as [string, string[]][]).map(([cat, ids]) => {
            // În timpul căutării secțiunile stau mereu deschise (colapsul ar ascunde rezultate).
            const isCollapsed = !searching && collapsed.has(cat);
            return (
            <div key={cat} className="pt-5">
              <button
                type="button"
                onClick={() => setCollapsed((prev) => {
                  const next = new Set(prev);
                  if (next.has(cat)) next.delete(cat); else next.add(cat);
                  return next;
                })}
                className="w-full text-[0.6875rem] font-bold uppercase tracking-[0.12em] text-neutral-500 dark:text-neutral-400 m-0 mb-2.5 flex items-center gap-2.5 cursor-pointer hover:text-neutral-700 dark:hover:text-neutral-200 transition-colors"
                aria-expanded={!isCollapsed}
              >
                <ChevronDown className={'h-3 w-3 transition-transform ' + (isCollapsed ? '-rotate-90' : '')} />
                <span>{CATEGORY_LABELS[cat as WidgetCategory]}</span>
                <span className="text-neutral-400 font-medium normal-case tracking-normal">({ids.length})</span>
                <span className="flex-1 h-px bg-neutral-200 dark:bg-neutral-800" />
              </button>
              {!isCollapsed && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {ids.map((id) => {
                  const def = WIDGET_REGISTRY[id]; if (!def) return null;
                  const added = visible.includes(id);
                  const size = (def as any).size as string | undefined;
                  return (
                    <button
                      key={id}
                      type="button"
                      disabled={added}
                      onClick={() => !added && onAdd(id)}
                      className={
                        'text-left bg-neutral-50 dark:bg-neutral-800 border rounded-[10px] p-3 transition-all relative ' +
                        (added
                          ? 'opacity-60 cursor-not-allowed border-transparent'
                          : 'border-transparent hover:bg-white dark:hover:bg-neutral-900 hover:border-primary hover:-translate-y-px cursor-pointer')
                      }
                    >
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="text-[0.7813rem] font-semibold text-neutral-900 dark:text-neutral-100">{def.title}</span>
                        <span className="inline-flex items-center gap-1.5 shrink-0">
                          {added ? (
                            <span className="text-[0.5625rem] font-bold tracking-[0.08em] uppercase text-emerald-600 inline-flex items-center gap-1">
                              <Check className="h-3 w-3" /> adăugat
                            </span>
                          ) : def.isNew ? (
                            <span className="text-[0.5625rem] font-bold tracking-[0.08em] uppercase text-primary">nou</span>
                          ) : null}
                          {size && (
                            <span
                              title={'Lățime pe grilă: ' + (({ S: 'mic (1/4)', M: 'mediu (1/3)', L: 'mare (1/2)', XL: 'lat (2/3)', Full: 'rând întreg' } as Record<string, string>)[size] ?? size)}
                              className="text-[0.5625rem] font-semibold text-neutral-400 dark:text-neutral-500 border border-neutral-200 dark:border-neutral-700 rounded px-1 py-px"
                            >
                              {size}
                            </span>
                          )}
                        </span>
                      </div>
                      <p className="text-[0.6563rem] text-neutral-500 dark:text-neutral-400 leading-[1.45] m-0">
                        {def.description}
                      </p>
                    </button>
                  );
                })}
              </div>
              )}
            </div>
            );
          })}
        </div>

        <div className="px-6 py-3 border-t border-neutral-200 dark:border-neutral-800 flex items-center justify-between bg-neutral-50 dark:bg-neutral-900/50">
          <span className="text-xs text-neutral-500 dark:text-neutral-400">
            {visible.length} din {WIDGET_ORDER.length} afișate
          </span>
          <button
            onClick={onReset}
            className="text-[0.75rem] font-medium text-neutral-600 dark:text-neutral-400 hover:text-primary transition-colors"
          >
            Revino la afișarea standard
          </button>
        </div>
      </div>
    </div>
  );
}
